<template>
  <div class="app-container">
    <el-card shadow="never" class="query-card">
      <!-- 筛选条件 -->
      <el-form :model="query" :inline="true" class="glass-form">
        <el-form-item :label="$t('common.consumptionDate')">
          <el-date-picker
            v-model="dateRange"
            type="daterange"
            size="small"
            style="width: 280px"
            :range-separator="$t('common.dateRangeSep')"
            :start-placeholder="$t('bookSummary.dateStart')"
            :end-placeholder="$t('common.dateEnd')"
            value-format="yyyy-MM-dd"
            :picker-options="datePickerOptions"
            @change="handleDateChange"
          />
        </el-form-item>
        <el-form-item :label="$t('consumption.namingStrategy')">
          <el-select
            v-model="query.strategyId"
            clearable
            filterable
            :placeholder="$t('consumption.namingStrategyPlaceholder')"
            size="small"
            style="width: 200px"
          >
            <el-option v-for="s in strategyList" :key="s.id" :label="s.strategyName" :value="s.id" />
          </el-select>
        </el-form-item>
        <el-form-item :label="$t('common.bookName')">
          <el-input
            v-model="query.bookName"
            :placeholder="$t('common.bookNameSearchPlaceholder')"
            clearable
            size="small"
            style="width: 200px"
            @keyup.enter.native="handleQuery"
          />
        </el-form-item>
        <el-form-item v-if="!isDataLimited" :label="$t('bookSummary.operatorName')">
          <el-select
            v-model="query.userName"
            clearable
            filterable
            :placeholder="$t('bookSummary.operatorPlaceholder')"
            size="small"
            style="width: 200px"
          >
            <el-option
              v-for="name in creatorOptions"
              :key="name"
              :label="name"
              :value="name"
            />
          </el-select>
        </el-form-item>
        <el-form-item>
          <el-button type="text" @click="showAdvanced = !showAdvanced">
            {{ showAdvanced ? $t('bookSummary.hideFilter') : $t('bookSummary.moreFilter') }}
            <i :class="showAdvanced ? 'el-icon-arrow-up' : 'el-icon-arrow-down'"></i>
          </el-button>
          <el-button type="primary" icon="el-icon-search" size="mini" @click="handleQuery">{{ $t('common.search') }}</el-button>
          <el-button icon="el-icon-refresh" size="mini" @click="resetQuery">{{ $t('common.reset') }}</el-button>
          <el-button type="warning" plain icon="el-icon-download" size="mini" :loading="exporting" @click="handleExport">导出 Excel</el-button>
        </el-form-item>
      </el-form>
      <!-- 指标范围筛选 -->
      <el-form v-show="showAdvanced" :model="query" :inline="true" class="glass-form metric-filter-row">
        <el-form-item :label="$t('bookSummary.spend')">
          <el-input-number v-model="query.spendMin" :placeholder="$t('common.min')" size="mini" :controls="false" style="width: 90px" />
          <span class="range-sep">~</span>
          <el-input-number v-model="query.spendMax" :placeholder="$t('common.max')" size="mini" :controls="false" style="width: 90px" />
        </el-form-item>
        <el-form-item :label="$t('bookSummary.ctr')">
          <el-input-number v-model="query.ctrMinPct" :placeholder="$t('common.min')" size="mini" :controls="false" :precision="2" style="width: 80px" />
          <span class="range-sep">~</span>
          <el-input-number v-model="query.ctrMaxPct" :placeholder="$t('common.max')" size="mini" :controls="false" :precision="2" style="width: 80px" />
          <span class="range-unit">%</span>
        </el-form-item>
        <el-form-item :label="$t('bookSummary.purchases')">
          <el-input-number v-model="query.purchasesMin" :placeholder="$t('common.min')" size="mini" :controls="false" :precision="0" style="width: 80px" />
          <span class="range-sep">~</span>
          <el-input-number v-model="query.purchasesMax" :placeholder="$t('common.max')" size="mini" :controls="false" :precision="0" style="width: 80px" />
        </el-form-item>
        <el-form-item :label="$t('bookSummary.costPerPurchase')">
          <el-input-number v-model="query.costPerPurchaseMin" :placeholder="$t('common.min')" size="mini" :controls="false" style="width: 90px" />
          <span class="range-sep">~</span>
          <el-input-number v-model="query.costPerPurchaseMax" :placeholder="$t('common.max')" size="mini" :controls="false" style="width: 90px" />
        </el-form-item>
        <el-form-item :label="$t('bookSummary.purchaseValue')">
          <el-input-number v-model="query.purchaseValueMin" :placeholder="$t('common.min')" size="mini" :controls="false" style="width: 90px" />
          <span class="range-sep">~</span>
          <el-input-number v-model="query.purchaseValueMax" :placeholder="$t('common.max')" size="mini" :controls="false" style="width: 90px" />
        </el-form-item>
        <el-form-item label="ROI">
          <el-input-number v-model="query.roiMin" :placeholder="$t('common.min')" size="mini" :controls="false" :precision="4" style="width: 90px" />
          <span class="range-sep">~</span>
          <el-input-number v-model="query.roiMax" :placeholder="$t('common.max')" size="mini" :controls="false" :precision="4" style="width: 90px" />
        </el-form-item>
      </el-form>

      <!-- 数据表格 -->
      <el-table
        v-loading="loading"
        :data="tableData"
        border
        size="small"
        :header-cell-style="{ background: '#f8fafd', color: '#606266', fontWeight: '600' }"
        @sort-change="handleSortChange"
      >
        <el-table-column type="index" label="#" width="50" align="center" />
        <el-table-column :label="$t('common.bookName')" prop="bookName" min-width="200" show-overflow-tooltip />
        <el-table-column :label="$t('bookSummary.spend')" width="120" align="right" sortable="custom" prop="spend">
          <template slot-scope="{ row }">{{ formatMoney(row.spend) }}</template>
        </el-table-column>
        <el-table-column :label="$t('bookSummary.ctr')" width="100" align="right" sortable="custom" prop="ctr">
          <template slot-scope="{ row }">{{ formatPercent(row.ctr) }}</template>
        </el-table-column>
        <el-table-column :label="$t('bookSummary.purchases')" width="90" align="right" sortable="custom" prop="purchases">
          <template slot-scope="{ row }">{{ formatInt(row.purchases) }}</template>
        </el-table-column>
        <el-table-column :label="$t('bookSummary.costPerPurchase')" width="140" align="right" sortable="custom" prop="costPerPurchase">
          <template slot-scope="{ row }">{{ formatMoney(row.costPerPurchase) }}</template>
        </el-table-column>
        <el-table-column :label="$t('bookSummary.purchaseValue')" width="120" align="right" sortable="custom" prop="purchaseValue">
          <template slot-scope="{ row }">{{ formatMoney(row.purchaseValue) }}</template>
        </el-table-column>
        <el-table-column label="ROI" width="100" align="right" sortable="custom" prop="roi">
          <template slot-scope="{ row }">{{ formatDecimal(row.roi) }}</template>
        </el-table-column>
        <el-table-column :label="$t('common.operation')" align="center" fixed="right" width="120">
          <template slot-scope="{ row }">
            <el-button size="mini" type="text" icon="el-icon-view" @click="handleViewMaterials(row)">{{ $t('bookSummary.viewDetail') }}</el-button>
          </template>
        </el-table-column>
      </el-table>

      <!-- 分页 -->
      <pagination
        v-show="total > 0"
        :total="total"
        :page.sync="pageNo"
        :limit.sync="pageSize"
        @pagination="fetchData"
      />

      <!-- 汇总行 -->
      <div class="summary-row" v-if="tableData.length > 0">
        <span class="summary-label">{{ $t('bookSummary.summaryLabel', { total: total }) }}</span>
        <span>{{ $t('bookSummary.spend') }} <b>{{ formatMoney(summary.spend) }}</b></span>
        <span>{{ $t('bookSummary.purchases') }} <b>{{ formatInt(summary.purchases) }}</b></span>
        <span>{{ $t('bookSummary.purchaseValue') }} <b>{{ formatMoney(summary.purchaseValue) }}</b></span>
      </div>
    </el-card>

    <!-- 素材明细弹窗 -->
    <el-dialog
      :title="materialDialogTitle"
      :visible.sync="materialVisible"
      width="960px"
      top="5vh"
      append-to-body
      destroy-on-close
    >
      <div v-loading="materialLoading">
        <div v-if="materialList.length > 0" class="material-card-grid">
          <div
            v-for="(item, idx) in materialList"
            :key="item.materialId"
            class="material-card"
          >
            <div class="card-media" @click="handlePreview(item)">
              <img
                v-if="item.type === 'image'"
                :src="item.filePath"
                class="card-thumb"
              />
              <img
                v-else-if="item.thumbnail"
                :src="item.thumbnail"
                class="card-thumb"
              />
              <div v-else class="card-thumb-placeholder">
                <i class="el-icon-video-camera"></i>
              </div>
              <span class="card-rank">#{{ idx + 1 }}</span>
              <span v-if="item.type === 'video'" class="video-badge">
                <i class="el-icon-video-play"></i>
              </span>
            </div>
            <div class="card-body">
              <div class="card-name" :title="item.name">{{ item.name }}</div>
              <div class="card-tags" v-if="item.tagNames && item.tagNames.length > 0">
                <el-tag
                  v-for="tag in item.tagNames"
                  :key="tag"
                  size="mini"
                  effect="plain"
                  class="card-tag"
                >{{ tag }}</el-tag>
              </div>
              <div class="card-metric-grid">
                <div class="metric-item">
                  <span class="metric-label">{{ $t('bookSummary.spend') }}</span>
                  <span class="metric-value">{{ formatMoney(item.spend) }}</span>
                </div>
                <div class="metric-item">
                  <span class="metric-label">{{ $t('bookSummary.ctr') }}</span>
                  <span class="metric-value">{{ formatPercent(item.ctr) }}</span>
                </div>
                <div class="metric-item">
                  <span class="metric-label">{{ $t('bookSummary.purchases') }}</span>
                  <span class="metric-value">{{ formatInt(item.purchases) }}</span>
                </div>
                <div class="metric-item">
                  <span class="metric-label">{{ $t('bookSummary.costPerPurchase') }}</span>
                  <span class="metric-value">{{ formatMoney(item.costPerPurchase) }}</span>
                </div>
                <div class="metric-item">
                  <span class="metric-label">{{ $t('bookSummary.purchaseValue') }}</span>
                  <span class="metric-value">{{ formatMoney(item.purchaseValue) }}</span>
                </div>
                <div class="metric-item metric-roi">
                  <span class="metric-label">ROI</span>
                  <span class="metric-value roi-value">{{ formatDecimal(item.roi) }}</span>
                </div>
              </div>
            </div>
          </div>
        </div>
        <el-empty v-else :description="$t('bookSummary.noMaterial')" />
      </div>
    </el-dialog>

    <!-- 图片预览 -->
    <el-dialog :visible.sync="previewVisible" append-to-body width="auto" top="5vh" class="preview-dialog">
      <img v-if="previewType === 'image'" :src="previewUrl" style="max-width: 100%; max-height: 80vh;" />
      <video v-else :src="previewUrl" controls autoplay style="max-width: 100%; max-height: 80vh;" />
    </el-dialog>
  </div>
</template>

<script>
import { queryInsightsAggPage, queryBookMaterials, exportInsightsAgg, queryCreatorOptions } from '@/api/report/insights';
import { listStrategies } from '@/api/report/strategyAnalysis';
import { confirmExportLargeRows } from '@/utils/export';
import { withLocalDevLongRangeDateShortcuts } from '@/utils/dateRangePickerOptions';
import Pagination from '@/components/Pagination';

export default {
  name: 'BookSummary',
  components: { Pagination },
  data() {
    return {
      loading: false,
      dateRange: [],
      datePickerOptions: withLocalDevLongRangeDateShortcuts([
        { text: this.$t('bookSummary.last7days'), onClick(picker) { const e = new Date(); const s = new Date(); s.setDate(s.getDate() - 6); picker.$emit('pick', [s, e]); } },
        { text: this.$t('bookSummary.last14days'), onClick(picker) { const e = new Date(); const s = new Date(); s.setDate(s.getDate() - 13); picker.$emit('pick', [s, e]); } },
        { text: this.$t('bookSummary.last30days'), onClick(picker) { const e = new Date(); const s = new Date(); s.setDate(s.getDate() - 29); picker.$emit('pick', [s, e]); } }
      ]),
      showAdvanced: false,
      query: {
        bookName: undefined,
        userName: undefined,
        strategyId: undefined,
        adAccountId: undefined,
        startDate: undefined,
        endDate: undefined,
        spendMin: undefined,
        spendMax: undefined,
        ctrMinPct: undefined,
        ctrMaxPct: undefined,
        purchasesMin: undefined,
        purchasesMax: undefined,
        costPerPurchaseMin: undefined,
        costPerPurchaseMax: undefined,
        purchaseValueMin: undefined,
        purchaseValueMax: undefined,
        roiMin: undefined,
        roiMax: undefined
      },
      pageNo: 1,
      pageSize: 10,
      total: 0,
      tableData: [],
      globalSummary: null,
      sortField: 'spend',
      sortOrder: 'desc',
      // 素材弹窗
      materialVisible: false,
      materialLoading: false,
      materialDialogTitle: '',
      materialList: [],
      currentBookName: '',
      // 预览
      previewVisible: false,
      previewUrl: '',
      previewType: 'image',
      exporting: false,
      creatorOptions: [],
      strategyList: []
    };
  },
  computed: {
    isDataLimited() {
      var appVm = this.$root && this.$root.$children && this.$root.$children[0];
      var user = appVm && appVm.currentUser;
      if (!user || !user.roleCodes) return false;
      return user.roleCodes.indexOf('data_limited') !== -1;
    },
    summary() {
      const s = this.globalSummary;
      if (s) {
        return {
          spend: Number(s.spend) || 0,
          purchases: Number(s.purchases) || 0,
          purchaseValue: Number(s.purchase_value) || 0
        };
      }
      let spend = 0, purchases = 0, purchaseValue = 0;
      for (const row of this.tableData) {
        spend += Number(row.spend) || 0;
        purchases += Number(row.purchases) || 0;
        purchaseValue += Number(row.purchaseValue) || 0;
      }
      return { spend, purchases, purchaseValue };
    }
  },
  created() {
    const end = new Date();
    const start = new Date();
    start.setDate(start.getDate() - 6);
    const fmt = (d) => d.getFullYear() + '-' + String(d.getMonth() + 1).padStart(2, '0') + '-' + String(d.getDate()).padStart(2, '0');
    this.dateRange = [fmt(start), fmt(end)];
    this.query.startDate = fmt(start);
    this.query.endDate = fmt(end);
    // 从「有素材明细的书籍」跳转时带入 bookName、adAccountId、日期
    const q = this.$route.query || {};
    if (q.bookName) this.query.bookName = q.bookName;
    if (q.adAccountId) this.query.adAccountId = q.adAccountId;
    if (q.startDate && q.endDate) {
      this.dateRange = [q.startDate, q.endDate];
      this.query.startDate = q.startDate;
      this.query.endDate = q.endDate;
    }
    this.loadCreatorOptions();
    this.loadStrategyList();
    this.fetchData();
  },
  methods: {
    async loadCreatorOptions() {
      try {
        const res = await queryCreatorOptions();
        this.creatorOptions = res.data || [];
      } catch (e) {
        this.creatorOptions = [];
      }
    },
    loadStrategyList() {
      listStrategies().then(function(res) {
        this.strategyList = res.data || [];
      }.bind(this));
    },
    async fetchData() {
      this.loading = true;
      try {
        const q = this.query;
        const params = {
          dimension: 'book',
          pageNo: this.pageNo,
          pageSize: this.pageSize,
          startDate: q.startDate,
          endDate: q.endDate,
          bookName: q.bookName,
          userName: q.userName,
          strategyId: q.strategyId,
          adAccountId: q.adAccountId,
          sortField: this.sortField,
          sortOrder: this.sortOrder,
          spendMin: q.spendMin,
          spendMax: q.spendMax,
          ctrMin: q.ctrMinPct != null ? q.ctrMinPct / 100 : undefined,
          ctrMax: q.ctrMaxPct != null ? q.ctrMaxPct / 100 : undefined,
          purchasesMin: q.purchasesMin,
          purchasesMax: q.purchasesMax,
          costPerPurchaseMin: q.costPerPurchaseMin,
          costPerPurchaseMax: q.costPerPurchaseMax,
          purchaseValueMin: q.purchaseValueMin,
          purchaseValueMax: q.purchaseValueMax,
          roiMin: q.roiMin,
          roiMax: q.roiMax
        };
        const res = await queryInsightsAggPage(params);
        const pageData = res.data || {};
        this.tableData = pageData.records || [];
        this.total = pageData.total || 0;
        this.globalSummary = pageData.summary || null;
      } finally {
        this.loading = false;
      }
    },
    handleQuery() {
      this.pageNo = 1;
      this.fetchData();
    },
    resetQuery() {
      const end = new Date();
      const start = new Date();
      start.setDate(start.getDate() - 6);
      const fmt = (d) => d.getFullYear() + '-' + String(d.getMonth() + 1).padStart(2, '0') + '-' + String(d.getDate()).padStart(2, '0');
      this.dateRange = [fmt(start), fmt(end)];
      this.query = {
        bookName: undefined,
        userName: undefined,
        strategyId: undefined,
        adAccountId: undefined,
        startDate: fmt(start),
        endDate: fmt(end),
        spendMin: undefined,
        spendMax: undefined,
        ctrMinPct: undefined,
        ctrMaxPct: undefined,
        purchasesMin: undefined,
        purchasesMax: undefined,
        costPerPurchaseMin: undefined,
        costPerPurchaseMax: undefined,
        purchaseValueMin: undefined,
        purchaseValueMax: undefined,
        roiMin: undefined,
        roiMax: undefined
      };
      this.pageNo = 1;
      this.sortField = 'spend';
      this.sortOrder = 'desc';
      this.fetchData();
    },
    handleDateChange(val) {
      if (val && val.length === 2) {
        this.query.startDate = val[0];
        this.query.endDate = val[1];
      } else {
        this.query.startDate = undefined;
        this.query.endDate = undefined;
      }
    },
    handleSortChange({ prop, order }) {
      this.sortField = prop || 'spend';
      this.sortOrder = order === 'ascending' ? 'asc' : 'desc';
      this.pageNo = 1;
      this.fetchData();
    },
    async handleExport() {
      this.$confirm('确认导出当前筛选条件下的数据？', '导出提示', {
        confirmButtonText: '确定',
        cancelButtonText: '取消',
        type: 'warning'
      }).then(async () => {
        const q = this.query;
        const countParams = {
          dimension: 'book',
          pageNo: 1,
          pageSize: 1,
          startDate: q.startDate,
          endDate: q.endDate,
          bookName: q.bookName,
          userName: q.userName,
          strategyId: q.strategyId,
          adAccountId: q.adAccountId,
          sortField: this.sortField,
          sortOrder: this.sortOrder,
          spendMin: q.spendMin,
          spendMax: q.spendMax,
          ctrMin: q.ctrMinPct != null ? q.ctrMinPct / 100 : undefined,
          ctrMax: q.ctrMaxPct != null ? q.ctrMaxPct / 100 : undefined,
          purchasesMin: q.purchasesMin,
          purchasesMax: q.purchasesMax,
          costPerPurchaseMin: q.costPerPurchaseMin,
          costPerPurchaseMax: q.costPerPurchaseMax,
          purchaseValueMin: q.purchaseValueMin,
          purchaseValueMax: q.purchaseValueMax,
          roiMin: q.roiMin,
          roiMax: q.roiMax
        };
        let countRes;
        try {
          countRes = await queryInsightsAggPage(countParams);
        } catch (e) {
          this.$message.error(this.$t('common.exportCountFail'));
          return;
        }
        try {
          await confirmExportLargeRows(this, (countRes.data || {}).total || 0);
        } catch (e) {
          return;
        }
        this.exporting = true;
        try {
          const params = {
            dimension: 'book',
            startDate: q.startDate,
            endDate: q.endDate,
            bookName: q.bookName,
            userName: q.userName,
            strategyId: q.strategyId,
            adAccountId: q.adAccountId,
            sortField: this.sortField,
            sortOrder: this.sortOrder,
            spendMin: q.spendMin,
            spendMax: q.spendMax,
            ctrMin: q.ctrMinPct != null ? q.ctrMinPct / 100 : undefined,
            ctrMax: q.ctrMaxPct != null ? q.ctrMaxPct / 100 : undefined,
            purchasesMin: q.purchasesMin,
            purchasesMax: q.purchasesMax,
            costPerPurchaseMin: q.costPerPurchaseMin,
            costPerPurchaseMax: q.costPerPurchaseMax,
            purchaseValueMin: q.purchaseValueMin,
            purchaseValueMax: q.purchaseValueMax,
            roiMin: q.roiMin,
            roiMax: q.roiMax
          };
          const res = await exportInsightsAgg(params);
          const blob = new Blob([res], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
          const url = window.URL.createObjectURL(blob);
          const link = document.createElement('a');
          link.href = url;
          link.download = '书籍汇总报表.xlsx';
          link.click();
          window.URL.revokeObjectURL(url);
          this.$message.success('导出成功');
        } catch (e) {
          this.$message.error('导出失败');
        } finally {
          this.exporting = false;
        }
      }).catch(() => {});
    },
    async handleViewMaterials(row) {
      const bookName = row.bookName || row.objectId;
      if (!bookName) {
        this.$message.warning(this.$t('bookSummary.noBookName'));
        return;
      }
      this.currentBookName = bookName;
      const dateInfo = this.query.startDate && this.query.endDate
        ? ' (' + this.query.startDate + ' ~ ' + this.query.endDate + ')'
        : '';
      this.materialDialogTitle = this.$t('bookSummary.materialDialogTitle') + bookName + dateInfo;
      this.materialVisible = true;
      this.materialLoading = true;
      this.materialList = [];
      try {
        const params = {
          bookName: bookName,
          startDate: this.query.startDate,
          endDate: this.query.endDate
        };
        if (row.adAccountId) {
          params.adAccountId = row.adAccountId;
        }
        const res = await queryBookMaterials(params);
        this.materialList = res.data || [];
      } catch (e) {
        this.$message.error(this.$t('bookSummary.loadMaterialFailed') + (e.message || ''));
      } finally {
        this.materialLoading = false;
      }
    },
    handlePreview(item) {
      this.previewType = item.type || 'image';
      this.previewUrl = item.filePath;
      this.previewVisible = true;
    },
    formatMoney(val) {
      if (val == null) return '-';
      return Number(val).toFixed(2);
    },
    formatDecimal(val) {
      if (val == null) return '-';
      return Number(val).toFixed(4);
    },
    formatPercent(val) {
      if (val == null) return '-';
      return (Number(val) * 100).toFixed(2) + '%';
    },
    formatInt(val) {
      if (val == null) return '-';
      return Number(val).toLocaleString();
    }
  }
};
</script>

<style scoped>
.summary-row {
  margin-top: 12px;
  padding: 10px 16px;
  background: #f5f7fa;
  border-radius: 4px;
  font-size: 13px;
  color: #606266;
  display: flex;
  gap: 20px;
  align-items: center;
  flex-wrap: wrap;
}
.summary-label {
  font-weight: 600;
  color: #303133;
}
.summary-row b {
  color: #409EFF;
}

/* 指标范围筛选行 */
.metric-filter-row {
  border-top: 1px dashed #DCDFE6;
  padding-top: 10px;
  margin-top: 4px;
}
.range-sep {
  margin: 0 4px;
  color: #909399;
}
.range-unit {
  margin-left: 2px;
  font-size: 12px;
  color: #909399;
}

/* 素材卡片网格 — 自适应列数 */
.material-card-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(200px, 1fr));
  gap: 16px;
  max-height: 72vh;
  overflow-y: auto;
  padding: 8px 4px;
}
.material-card {
  border-radius: 10px;
  overflow: hidden;
  background: #fff;
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.08);
  transition: transform 0.2s, box-shadow 0.2s;
}
.material-card:hover {
  transform: translateY(-3px);
  box-shadow: 0 6px 20px rgba(0, 0, 0, 0.12);
}

/* 媒体区 */
.card-media {
  position: relative;
  width: 100%;
  padding-top: 100%;
  overflow: hidden;
  background: linear-gradient(135deg, #f5f7fa 0%, #e4e7ed 100%);
  cursor: pointer;
}
.card-thumb {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  object-fit: cover;
  transition: transform 0.3s;
}
.card-media:hover .card-thumb {
  transform: scale(1.05);
}
.card-thumb-placeholder {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 40px;
  color: #c0c4cc;
}
.card-rank {
  position: absolute;
  top: 8px;
  left: 8px;
  background: rgba(0, 0, 0, 0.55);
  color: #fff;
  border-radius: 6px;
  padding: 2px 8px;
  font-size: 11px;
  font-weight: 600;
  letter-spacing: 0.5px;
}
.video-badge {
  position: absolute;
  bottom: 8px;
  right: 8px;
  background: rgba(0, 0, 0, 0.5);
  color: #fff;
  border-radius: 50%;
  width: 28px;
  height: 28px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 16px;
}

/* 内容区 */
.card-body {
  padding: 12px 14px 14px;
}
.card-name {
  font-size: 13px;
  font-weight: 600;
  color: #303133;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  margin-bottom: 8px;
  line-height: 1.4;
}
.card-tags {
  margin-bottom: 10px;
  display: flex;
  flex-wrap: wrap;
  gap: 4px;
}
.card-tag {
  border-radius: 3px;
}

/* 指标网格：2列 */
.card-metric-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 6px 12px;
}
.metric-item {
  display: flex;
  flex-direction: column;
}
.metric-label {
  font-size: 11px;
  color: #909399;
  line-height: 1.3;
}
.metric-value {
  font-size: 13px;
  font-weight: 600;
  color: #303133;
  line-height: 1.4;
}
.metric-roi {
  grid-column: 1 / -1;
  border-top: 1px solid #f0f2f5;
  padding-top: 6px;
  margin-top: 2px;
  flex-direction: row;
  align-items: center;
  gap: 6px;
}
.roi-value {
  font-size: 16px;
  color: #E6A23C;
}

.preview-dialog >>> .el-dialog__body {
  text-align: center;
  padding: 10px;
}
</style>
